'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { normalizePath } from '../_utils';
import {
  createFolder as createClawFolder,
  deleteFileOrFolder,
  getDownloadUrl,
  listFolder as listClawFolder,
} from '../apis';
import type { ClawFileItem } from '../types';
import { useUploadClawFile } from './use-upload';

interface ListClawFolderOptions {
  folderPath?: string;
  pageSize?: number;
  pageToken?: string;
}

interface CreateClawFolderOptions {
  name: string;
  parentPath?: string;
}

type DeleteClawTarget = ClawFileItem | { path: string; type: 'file' | 'folder' };

interface UploadClawFilesOptions {
  files: File[];
  targetFolderPath?: string;
  isAllowedFile?: (file: File) => boolean;
}

export interface UploadClawFilesResult {
  successCount: number;
  invalidFileNameList: string[];
  conflictFileNameList: string[];
  failFileNameList: string[];
  /** 内容审核未通过的文件名列表 */
  moderateRejectedFileNameList: string[];
  /** 任一上传请求被服务端判定 claw 已失效（reason: claw_is_inactive） */
  clawInactive: boolean;
}

export const useClawFolder = (clawId?: string) => {
  const { uploadClawFile } = useUploadClawFile();
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [deletingPath, setDeletingPath] = useState('');
  const [previewUrlMap, setPreviewUrlMap] = useState<Record<string, string>>({});
  const previewRequestMapRef = useRef<Record<string, Promise<string | null>>>({});

  useEffect(() => {
    setPreviewUrlMap({});
    previewRequestMapRef.current = {};
  }, [clawId]);

  const listFolder = useCallback(
    async ({ folderPath, pageSize = 100, pageToken }: ListClawFolderOptions = {}) => {
      if (!clawId) {
        return {
          data: null,
          error: true,
        };
      }

      const normalizedPath = normalizePath(folderPath);
      const items: ClawFileItem[] = [];
      const visitedPageTokens = new Set<string>();
      let nextPageToken = pageToken;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await listClawFolder({
          claw_id: clawId,
          folder_path: normalizedPath || undefined,
          pagesz: pageSize,
          page_token: nextPageToken || undefined,
        });

        if (error || !data) {
          return {
            data: null,
            error,
          };
        }

        items.push(...(data.items ?? []));

        const normalizedNextPageToken = data.next_page_token?.trim();
        if (!normalizedNextPageToken || visitedPageTokens.has(normalizedNextPageToken)) {
          hasMore = false;
          continue;
        }

        visitedPageTokens.add(normalizedNextPageToken);
        nextPageToken = normalizedNextPageToken;
      }

      return {
        data: items,
        error: null,
      };
    },
    [clawId],
  );

  const createFolder = useCallback(
    async ({ name, parentPath }: CreateClawFolderOptions) => {
      const trimmedName = name.trim();
      if (!clawId || !trimmedName || creatingFolder) {
        return {
          data: null,
          error: {
            reason: 'InvalidRequest',
          },
        };
      }

      setCreatingFolder(true);
      const result = await createClawFolder({
        claw_id: clawId,
        name: trimmedName,
        parent_path: normalizePath(parentPath) || './',
      });
      setCreatingFolder(false);
      return result;
    },
    [clawId, creatingFolder],
  );

  const deleteEntry = useCallback(
    async (target: DeleteClawTarget) => {
      const itemPath = normalizePath(target.path);
      if (!clawId || !itemPath || deletingPath) {
        return {
          error: true,
        };
      }

      const targetType = target.type;
      setDeletingPath(itemPath);
      const result = await deleteFileOrFolder({
        claw_id: clawId,
        path: itemPath + (targetType === 'folder' ? '/' : ''),
        type: targetType,
      });
      setDeletingPath('');

      if (!result.error) {
        setPreviewUrlMap((prev) => {
          const next = { ...prev };
          const prefix = `${itemPath}/`;
          let changed = false;

          Object.keys(next).forEach((cacheKey) => {
            if (cacheKey === itemPath || cacheKey.startsWith(prefix)) {
              delete next[cacheKey];
              changed = true;
            }
          });

          return changed ? next : prev;
        });
      }

      return result;
    },
    [clawId, deletingPath],
  );

  const uploadFiles = useCallback(
    async ({ files, targetFolderPath, isAllowedFile }: UploadClawFilesOptions): Promise<UploadClawFilesResult> => {
      if (!clawId || uploadingFile || !files.length) {
        return {
          successCount: 0,
          invalidFileNameList: [],
          conflictFileNameList: [],
          failFileNameList: [],
          moderateRejectedFileNameList: [],
          clawInactive: false,
        };
      }

      const validFiles = isAllowedFile ? files.filter((file) => isAllowedFile(file)) : files;
      const invalidFileNameList = isAllowedFile
        ? files.filter((file) => !isAllowedFile(file)).map((file) => file.name)
        : [];

      if (!validFiles.length) {
        return {
          successCount: 0,
          invalidFileNameList,
          conflictFileNameList: [],
          failFileNameList: [],
          moderateRejectedFileNameList: [],
          clawInactive: false,
        };
      }

      setUploadingFile(true);
      const uploadResults = await Promise.allSettled(
        validFiles.map((file) =>
          uploadClawFile({
            clawId,
            file,
            folderPath: normalizePath(targetFolderPath),
          }),
        ),
      );
      setUploadingFile(false);

      let successCount = 0;
      let clawInactive = false;
      const conflictFileNameList: string[] = [];
      const failFileNameList: string[] = [];
      const moderateRejectedFileNameList: string[] = [];

      uploadResults.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          const { data, error } = result.value;
          if (data) {
            successCount += 1;
            return;
          }

          if (error?.reason === 'claw_is_inactive') {
            clawInactive = true;
            // claw 失效是 claw 级状态问题，不再把对应文件名计入"上传失败"列表，
            // 避免与统一的 inactive 提示叠加展示
            return;
          }

          if (error?.reason === 'moderate_rejected') {
            if (error.fileName) {
              moderateRejectedFileNameList.push(error.fileName);
            }
            return;
          }

          if (error?.reason === 'Conflict') {
            if (error.fileName) {
              conflictFileNameList.push(error.fileName);
            }
            return;
          }

          if (error?.fileName) {
            failFileNameList.push(error.fileName);
            return;
          }
        }

        const fileName = validFiles[index]?.name;
        if (fileName) {
          failFileNameList.push(fileName);
        }
      });

      return {
        successCount,
        invalidFileNameList,
        conflictFileNameList,
        failFileNameList,
        moderateRejectedFileNameList,
        clawInactive,
      };
    },
    [clawId, uploadingFile],
  );

  const cachePreviewUrl = useCallback((filePath: string, url: string) => {
    const normalizedPath = normalizePath(filePath);
    if (!normalizedPath || !url) return;

    setPreviewUrlMap((prev) => {
      if (prev[normalizedPath] === url) return prev;
      return {
        ...prev,
        [normalizedPath]: url,
      };
    });
  }, []);

  const getCachedPreviewUrl = useCallback(
    (filePath: string) => previewUrlMap[normalizePath(filePath)],
    [previewUrlMap],
  );

  const invalidatePreviewUrl = useCallback((filePath: string) => {
    const normalizedPath = normalizePath(filePath);
    if (!normalizedPath) return;

    setPreviewUrlMap((prev) => {
      if (!prev[normalizedPath]) return prev;
      const next = { ...prev };
      delete next[normalizedPath];
      return next;
    });
  }, []);

  const getPreviewUrl = useCallback(
    async (filePath: string): Promise<string | null> => {
      const normalizedPath = normalizePath(filePath);
      if (!clawId || !normalizedPath) return null;

      const cachedUrl = previewUrlMap[normalizedPath];
      if (cachedUrl) return cachedUrl;

      const pendingRequest = previewRequestMapRef.current[normalizedPath];
      if (pendingRequest) return pendingRequest;

      const request = getDownloadUrl({
        query: {
          claw_id: clawId,
          file_path: normalizedPath,
        },
      })
        .then(({ data, error }) => {
          if (error || !data?.download_url) {
            return null;
          }

          cachePreviewUrl(normalizedPath, data.download_url);
          return data.download_url as string;
        })
        .finally(() => {
          delete previewRequestMapRef.current[normalizedPath];
        });

      previewRequestMapRef.current[normalizedPath] = request;
      return request;
    },
    [clawId, previewUrlMap],
  );

  return {
    creatingFolder,
    uploadingFile,
    deletingPath,
    listFolder,
    createFolder,
    deleteEntry,
    uploadFiles,
    getPreviewUrl,
    getCachedPreviewUrl,
    cachePreviewUrl,
    invalidatePreviewUrl,
  };
};
